import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import useTracking from '@gameday/utils/hooks/useTracking';
import Tabs from '../../Tabs/Tabs';
import BoxscoreDataContainer from '../../Boxscore/BoxscoreDataContainer';
import { BoxScoreViewTypes } from '../../Boxscore/Boxscore.style';
import SummaryDataContainer from '../../Summary/containers/SummaryDataContainer';
import MatchupDataContainer from '../../Matchup/containers/MatchupDataContainer';
import LiveFeedDataContainer from '../../LiveFeed/containers/LiveFeedDataContainer';
import {
    LiveSummaryBoxWrapper,
    InfoDivider,
    MainTabsWrapper,
    MatchupInfoDivider,
    StretchOverPaddingWrapper,
    ViewScrollWrapper,
    BoxTabHeaderWrapper,
    BoxTabHeaderDivider,
    LimitedFunctionalityChip,
    LiveBoxscoreWrapper
} from '../Live.style';
import { CardWrapper } from '../../Card/Card.style';
import { liveViewsTypes } from '../../themes/liveViewsTypes';
import LineScoreDataContainer from '../../LineScore/containers/LineScoreDataContainer';
import PlayerMatchupDataContainer from '../../PlayerMatchup/containers/PlayerMatchupDataContainer';
import changeViewAction from '@gameday/app/lib/store/actions/views';
import { getLiveState } from '../store/selectors/liveSelector';
import { HighlightsPlaylistDataContainer } from '../../HighlightsPlaylist/containers/HighlightsPlaylistDataContainer';
import DynamicFieldViewDataContainer from '../../DynamicFieldView/containers/DynamicFieldViewDataContainer';
import LiveViewsDFVDataContainer from '../containers/LiveViewsDFVDataContainer';
import { getDynamicFieldView } from '../../DynamicFieldView/store/selectors/getDynamicFieldView';
import useNativeLoginFunc from '@gameday/utils/hooks/useNativeLoginFunc';
import useIntersectionObserver from '@react-hook/intersection-observer';
import getAutoPlayMuteSetting from '@gameday/utils/selectors/getAutoPlayMuteSetting';
import ToastMessageWrapper from '../../Promos/components/ToastMessageWrapper';
import PlayTimerProvider from '@gameday/utils/providers/PlayTimerProvider';

const LIVE = 'live';
const { SINGLENARROW, SINGLEWIDE, FULL, SINGLE, FULLWIDE, FULLNARROW } = liveViewsTypes;
const tabTranslator = function (item, index) {
    const { key, text, id } = item;
    const translatedText = this(key, text);

    return {
        text,
        name: translatedText,
        id
    };
};

const items = [{
    text: 'Live',
    id: 'live',
    key: 'label_live'
}, {
    text: 'Summary',
    id: 'summary',
    key: 'label_summary'
}, {
    text: 'Box',
    id: 'box',
    key: 'button_box'
}, {
    text: 'Video',
    id: 'video',
    key: 'button_video'
}];

const itemsFull = [{
    text: 'Live',
    id: 'live',
    key: 'label_live'
}, {
    text: 'Summary',
    id: 'summary',
    key: 'label_summary'
}, {
    text: 'Video',
    id: 'video',
    key: 'button_video'
}];

const getTabs = (translate, type, enableTabsLive = {}) => {
    return (type === FULL || type === FULLNARROW || type === FULLWIDE ? itemsFull : items)
        .filter((tab) => enableTabsLive[tab.id] !== false)
        .map(tabTranslator, translate);
};

const LiveTabs = ({
    openAtBatOverlay,
    type,
    onAuthOpen,
    showExplore,
    firstRunUser,
    showLimitedFunctionalityChip
}) => {
    const Tracking = useTracking();
    const translate = useTranslate();
    const dispatch = useDispatch();
    const [isToastMessageSticky, setToastMessageSticky] = useState(true);
    const [promoRef, setPromoPRef] = useState(null);
    const { enableDFVDesktop, inWarmupWindow } = useSelector(getDynamicFieldView);
    const { game_tab, abstractGameState, enableTabsLive } = useSelector(getLiveState);
    const { videoTabAutoPlay, videoTabMute, appAutoPlay } = useSelector(getAutoPlayMuteSetting('live'));
    const extendedDisplay = (type === SINGLE || type === SINGLEWIDE);
    const [isWarmup, setIsWarmup] = useState(null);
    const nativeLoginFunc = useNativeLoginFunc();
    let warmupTwoMins;
    function warmupTimer () {
        clearTimeout(warmupTwoMins);
        const twoMins = 120000;
        warmupTwoMins = setTimeout(() => {
            setIsWarmup(false);
        }, twoMins);
    }

    useEffect(() => {
        if (inWarmupWindow) {
            setIsWarmup(true);
            warmupTimer();
        }
    }, [enableDFVDesktop, inWarmupWindow]);

    // refs
    const tabsRef = useRef(null);
    const viewScrollRef = useRef(null);
    const matchupRef = useRef(null);

    const [isStuck, setIsStuck] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(LIVE);

    const isSingleCol = type === SINGLE || type === SINGLENARROW || type === SINGLEWIDE;
    const isTablet = type === SINGLE || type === SINGLEWIDE;

    const tabsData = useMemo(() => getTabs(translate, type, enableTabsLive), [translate, type]);

    // Options for the IntersectionObserver. The root is the ViewScroll (parent wrapper of tabs and content).
    // Threshold set to 1 meaning any pixel of the target (matchup) that is past ViewScroll will count as !isIntersecting
    // Because matchup stickies to the bottom of ViewScroll, we add -1px to the margin so that it triggers.
    // Adding 130px so that it doesn't intersect from the bottom.
    const options = useMemo(() => ({
        root: viewScrollRef?.current,
        rootMargin: '-1px 0px 130px 0px',
        threshold: 1
    }), [viewScrollRef?.current]);

    const { isIntersecting } = useIntersectionObserver(matchupRef, options);

    useEffect(() => {
        setIsStuck(!isIntersecting);
    }, [isIntersecting]);

    const tabsTopDistance = viewScrollRef?.current?.getBoundingClientRect().top;

    const limitedFunctionalityMessage = translate('gameday_limited_message', 'Limited Gameday Functionality');

    const boxscoreStyle = type === SINGLEWIDE ? BoxScoreViewTypes.WIDE : BoxScoreViewTypes.NARROW;
    const displayMatchupDivider = abstractGameState === 'Final' && type === SINGLENARROW;
    const handleTabChange = useCallback((e) => {
        if (isTablet) {
            viewScrollRef.current.scrollTop = 0;
        }
        const sIndex = e?.currentTarget.dataset.index * 1;
        setSelectedIndex(e?.currentTarget?.id);
        const tabId = e?.currentTarget?.id;
        const cfg = {
            componentName: [tabId],
            selectedIndex: sIndex,
            text: tabId,
            activity: 'Tab Click'
        };

        Tracking.track.track_click(cfg);
        Tracking.track.track_pageload();

        dispatch(changeViewAction({
            detail: '',
            game_tab: tabId,
            game_view: LIVE,
            updateRoute: true
        }));
    }, [Tracking, setSelectedIndex]);

    const MatchupContainer = useMemo(() => {
        // eslint-disable-next-line react/display-name, react/prop-types
        return ({ children, stuck }) => (isTablet)
            ? <CardWrapper ref={matchupRef} $matchup={true} className={stuck ? 'stuck matchup-card' : 'matchup-card'}>
                {children}
            </CardWrapper>
            : <>{children}</>;
    }, [isTablet]);

    const tabbedView = useMemo(() => {
        const tabId = tabsData.filter((tab) => tab.id === selectedIndex)?.[0]?.id || LIVE;
        const shouldShowPromo = (!game_tab && selectedIndex === LIVE) || (game_tab === LIVE && selectedIndex === LIVE);
        switch (tabId) {
            case 'live':
                return (
                    <>
                        { isSingleCol && <LiveViewsDFVDataContainer
                            onAuthOpen={onAuthOpen}
                            type={type}
                            firstRunUser={firstRunUser}
                            showExplore={showExplore}
                            nativeLoginFunc={nativeLoginFunc}
                        />}
                        { isSingleCol && showLimitedFunctionalityChip && <LimitedFunctionalityChip $type={type}> {limitedFunctionalityMessage} </LimitedFunctionalityChip> }
                        { isSingleCol && <MatchupContainer stuck={isStuck}><MatchupDataContainer /></MatchupContainer> }
                        { displayMatchupDivider && <MatchupInfoDivider /> }
                        <CardWrapper $displayFlat={!isTablet} $maxHeightOverride='max-content'>
                            {!isSingleCol && enableDFVDesktop && !isWarmup && <DynamicFieldViewDataContainer renderStandalone={false} /> }
                            <PlayTimerProvider adjusted={isSingleCol}>
                                <LiveFeedDataContainer isSingleCol={isSingleCol} reverseChron={ true } showAnimations={ true } openAtBatOverlay={ openAtBatOverlay } />
                            </PlayTimerProvider>
                        </CardWrapper>
                        {shouldShowPromo && <ToastMessageWrapper isSingleCol={isSingleCol} />}
                    </>
                );
            case 'summary':
                return <SummaryDataContainer openAtBatOverlay={ openAtBatOverlay } useContainer={isTablet} scrollRef={viewScrollRef} />;
            case 'box':
                return (
                    <>
                        <BoxTabHeaderWrapper $type={type}>
                            <LineScoreDataContainer />
                            {isTablet && (
                                <>
                                    <BoxTabHeaderDivider />
                                    <PlayerMatchupDataContainer />
                                </>
                            )}
                        </BoxTabHeaderWrapper>
                        <LiveBoxscoreWrapper $type={type}>
                            <BoxscoreDataContainer viewType={boxscoreStyle} showBench={true} showBullpen={true} />
                        </LiveBoxscoreWrapper>
                    </>
                );
            case 'video':
                return <HighlightsPlaylistDataContainer extendedDisplay={extendedDisplay} autoPlay={videoTabAutoPlay} mute={videoTabMute} appAutoPlay={appAutoPlay} />;
        }
    }, [type, selectedIndex, openAtBatOverlay, tabsData, firstRunUser, boxscoreStyle, displayMatchupDivider, isStuck, MatchupContainer, isTablet, nativeLoginFunc, enableDFVDesktop, isWarmup, videoTabAutoPlay, videoTabMute, tabsData, game_tab]);

    useEffect(() => {
        // deep linking subview
        if (game_tab) {
            tabsData.forEach((tab) => {
                if (tab.id === game_tab) {
                    setSelectedIndex(game_tab);
                }
            });
        }
    }, [game_tab, tabsData]);

    return (
        <LiveSummaryBoxWrapper ref={tabsRef} $type={type}>
            <StretchOverPaddingWrapper>
                <MainTabsWrapper type={type}>
                    <Tabs activeTab={tabsData.filter((tab) => tab.id === selectedIndex)?.[0]?.name || 'Live'} items={tabsData} handleTabChange={handleTabChange} key={`lsb-tab-${tabsData[selectedIndex]?.id}`}/>
                </MainTabsWrapper>
                <InfoDivider type={type} />
            </StretchOverPaddingWrapper>
            <ViewScrollWrapper ref={viewScrollRef} $top={isTablet && tabsTopDistance}>
                {tabbedView}
            </ViewScrollWrapper>
        </LiveSummaryBoxWrapper>
    );
};

LiveTabs.propTypes = {
    openAtBatOverlay: PropTypes.func,
    type: PropTypes.string,
    onAuthOpen: PropTypes.func,
    showExplore: PropTypes.func,
    firstRunUser: PropTypes.bool,
    showLimitedFunctionalityChip: PropTypes.bool
};

export default LiveTabs;
